import React, { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import {
  Box, Typography, Paper, Chip, Skeleton, Stack, TextField, MenuItem, Autocomplete,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SmsIcon from '@mui/icons-material/Sms';
import { __t } from '../../../i18n/t';
import { Ask, Hint } from '../../../components/wizardKit/basics';
import { CheckRow, Field, LabeledInput } from '../../../components/wizardKit/controls';
import { C } from '../../../components/wizardKit/tokens';
import { TagType } from '../../../models';

export interface AudienceValue {
  segment: 'at_risk' | 'inactive' | 'custom';
  minDaysWithoutFueling: number;
  maxDaysWithoutFueling: number | null;
  includeNeverFueled: boolean;
  tagIds: string[];
  maxCustomers: number | null;
}

interface AudienceBucket { label: string; count: number; }
interface AudienceSummary { total: number; withApp: number; withCellphone: number; byDaysBucket: AudienceBucket[]; }
interface Tag { id: string; name: string; color?: string; }

interface AudienceStepProps {
  chainId: string;
  /** Nome e descrição da campanha moram aqui: são a primeira pergunta do cadastro. */
  name: string;
  description: string;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  audience: AudienceValue;
  onChange: (patch: Partial<AudienceValue>) => void;
  audienceSummary: AudienceSummary | null;
  loading: boolean;
  unavailableReason?: string | null;
}

// Ritmo do cadastro em etapas (o mesmo do desconto v2): 28 px entre perguntas.
const containerSx = { display: 'flex', flexDirection: 'column', gap: 3.5, pt: 2.25, pb: 3.5, maxWidth: 968 };
const rowSx = { display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' };
const panelSx = { p: 2.25, maxWidth: 640 };

// Fora do JSX: o plugin de i18n embrulha literal de texto dentro de ternário (ver wizardKit/tokens).
const TOTAL_SX = { color: C.ink };
const TOTAL_ZERO_SX = { color: 'error.main' };
const BUCKET_CHIP_SX = { bgcolor: C.violetSoft, color: C.violet, border: '1px solid rgba(68,51,180,.16)' };

const segmentChoices: Array<{ id: AudienceValue['segment']; name: string }> = [
  { id: 'at_risk', name: 'Em risco' },
  { id: 'inactive', name: 'Inativos' },
  { id: 'custom', name: 'Personalizado' },
];

const AudienceStep: React.FC<AudienceStepProps> = ({
  chainId, name, description, onNameChange, onDescriptionChange,
  audience, onChange, audienceSummary, loading, unavailableReason,
}) => {
  const dataProvider = useDataProvider();
  const [tags, setTags] = useState<Tag[]>([]);
  const noCeiling = audience.maxDaysWithoutFueling === null;
  const noLimit = audience.maxCustomers === null;

  useEffect(() => {
    dataProvider
      .getList('chains/chainId/tag', {
        pagination: { page: 1, perPage: 999 },
        sort: { field: 'name', order: 'ASC' },
        filter: { type: TagType.client, chainId },
      })
      .then(({ data }: any) => setTags(data))
      .catch(() => {});
  }, [dataProvider, chainId]);

  return (
    <Box sx={containerSx}>
      <Ask
        id="q-nome"
        title="Como se chama esta campanha?"
        help={__t('Só para você encontrá-la depois na lista.')}
      >
        <Box sx={rowSx}>
          <LabeledInput
            id="campanha-nome"
            label="Nome"
            width={320}
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            right={<Hint sx={{ fontSize: 12 }}>obrigatório</Hint>}
          />
          <LabeledInput
            id="campanha-descricao"
            label="Descrição (opcional)"
            width={420}
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
          />
        </Box>
      </Ask>

      <Ask
        id="q-segmento"
        title="Quem entra nesta campanha?"
        help={__t('Quem entra é decidido agora: o público não muda depois de criada.')}
      >
        <Field id="publico-segmento" label="Segmento" width={280}>
          <TextField
            select
            id="publico-segmento"
            fullWidth
            value={audience.segment}
            onChange={(e) => onChange({ segment: e.target.value as AudienceValue['segment'] })}
          >
            {segmentChoices.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
        </Field>
      </Ask>

      <Ask id="q-dias" title="Há quanto tempo sem abastecer?">
        <Box sx={rowSx}>
          <LabeledInput
            id="publico-dias-de"
            label="A partir de"
            suffix={__t('dias')}
            inputMode="numeric"
            width={180}
            value={String(audience.minDaysWithoutFueling)}
            onChange={(e) => onChange({ minDaysWithoutFueling: +e.target.value || 1 })}
          />
          <LabeledInput
            id="publico-dias-ate"
            label="Até"
            suffix={__t('dias')}
            inputMode="numeric"
            width={180}
            disabled={noCeiling}
            value={noCeiling ? '' : String(audience.maxDaysWithoutFueling)}
            onChange={(e) => onChange({ maxDaysWithoutFueling: e.target.value ? +e.target.value : null })}
          />
        </Box>
        <CheckRow
          checked={noCeiling}
          onChange={() => onChange({ maxDaysWithoutFueling: noCeiling ? audience.minDaysWithoutFueling + 30 : null })}
          desc={__t('Inclui quem parou há muito tempo.')}
        >
          Sem teto de dias
        </CheckRow>
        <CheckRow
          checked={audience.includeNeverFueled}
          onChange={() => onChange({ includeNeverFueled: !audience.includeNeverFueled })}
          desc={__t('Ativação não é recuperação — use com cuidado.')}
        >
          Incluir quem nunca abasteceu
        </CheckRow>
      </Ask>

      <Ask id="q-filtros" title="Quer estreitar mais o público?" help={__t('Os dois são opcionais.')}>
        <Field id="publico-etiquetas" label="Etiquetas (qualquer uma delas)" width={480}>
          <Autocomplete
            multiple
            options={tags}
            value={tags.filter((t) => audience.tagIds.includes(t.id))}
            onChange={(_, value) => onChange({ tagIds: value.map((t) => t.id) })}
            getOptionLabel={(t) => t.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderTags={(value, getTagProps) =>
              value.map((t, index) => <Chip label={t.name} size="small" {...getTagProps({ index })} key={t.id} />)}
            renderInput={(params) => <TextField {...params} id="publico-etiquetas" placeholder="Selecione" />}
          />
        </Field>
        <CheckRow
          checked={noLimit}
          onChange={() => onChange({ maxCustomers: noLimit ? 10000 : null })}
        >
          Sem limite de clientes
        </CheckRow>
        {!noLimit && (
          <LabeledInput
            id="publico-limite"
            label="Limite de clientes"
            inputMode="numeric"
            width={200}
            hint="Prioriza quem parou há menos tempo."
            value={audience.maxCustomers === null ? '' : String(audience.maxCustomers)}
            onChange={(e) => onChange({ maxCustomers: e.target.value ? +e.target.value : null })}
          />
        )}
      </Ask>

      <Paper variant="outlined" sx={panelSx}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <PeopleAltIcon fontSize="small" sx={TOTAL_SX} />
          <Typography variant="subtitle2" fontWeight={700}>Contagem ao vivo</Typography>
        </Stack>
        {loading ? (
          <Skeleton variant="rectangular" height={60} />
        ) : unavailableReason ? (
          <Typography variant="body2" color="text.secondary">{unavailableReason}</Typography>
        ) : audienceSummary ? (
          <>
            {/* O verde da marca em texto grande não chega a 4,5:1 sobre branco: o número é tinta. */}
            <Typography variant="h5" fontWeight={700} sx={audienceSummary.total === 0 ? TOTAL_ZERO_SX : TOTAL_SX}>
              {audienceSummary.total} cliente{audienceSummary.total !== 1 ? 's' : ''}
            </Typography>
            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
              <Chip icon={<PhoneIphoneIcon />} size="small" label={`${audienceSummary.withApp} com app`} variant="outlined" />
              <Chip icon={<SmsIcon />} size="small" label={`${audienceSummary.withCellphone} com celular`} variant="outlined" />
            </Stack>
            {audienceSummary.byDaysBucket.length > 0 && (
              <Stack direction="row" spacing={0.5} mt={1.5} flexWrap="wrap">
                {audienceSummary.byDaysBucket.map((b) => (
                  <Chip key={b.label} size="small" label={`${b.label}: ${b.count}`} sx={BUCKET_CHIP_SX} />
                ))}
              </Stack>
            )}
            {audienceSummary.total === 0 && (
              <Typography variant="body2" color="error.main" mt={1}>
                Nenhum cliente no público. Ajuste os filtros antes de continuar.
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Ajuste os filtros para ver a contagem.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default AudienceStep;
